import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AiFillWarning } from 'react-icons/ai'
import { IoCloseOutline } from 'react-icons/io5'
import { MarginType } from '@/types/Margin'
import { LIQUIDATION_ALERT_EVENT, type LiquidationAlertDetail } from '@/utils/notificationUtils'

// Displays when useOrdersSubscription detects a LIQUIDATION order modifier and emits LIQUIDATION_ALERT_EVENT.
type LiquidationAlert = LiquidationAlertDetail & {
  occurrences: number
}

export function LiquidationAlertOverlay() {
  const navigate = useNavigate()
  const [alertsByAccount, setAlertsByAccount] = useState<Map<string, LiquidationAlert>>(
    () => new Map(),
  )

  useEffect(() => {
    function handleAlert(event: Event) {
      const { detail } = event as CustomEvent<LiquidationAlertDetail>
      if (!detail?.accountId || !detail.orderBookId) return

      setAlertsByAccount((currentAlerts) => {
        const nextAlerts = new Map(currentAlerts)
        const currentAlert = nextAlerts.get(detail.accountId)
        nextAlerts.set(detail.accountId, {
          ...detail,
          occurrences: (currentAlert?.occurrences ?? 0) + 1,
        })
        return nextAlerts
      })
    }

    window.addEventListener(LIQUIDATION_ALERT_EVENT, handleAlert)

    return () => {
      window.removeEventListener(LIQUIDATION_ALERT_EVENT, handleAlert)
    }
  }, [])

  function dismissAlert(accountId: string) {
    setAlertsByAccount((currentAlerts) => {
      const nextAlerts = new Map(currentAlerts)
      nextAlerts.delete(accountId)
      return nextAlerts
    })
  }

  function switchToAffectedAccount(alert: LiquidationAlert) {
    dismissAlert(alert.accountId)
    void navigate({
      to: '/trade/$orderBookId',
      params: { orderBookId: alert.orderBookId },
      search: {
        marginType: MarginType.ISOLATED,
        positionId: alert.accountId,
      },
    })
  }

  const alerts = Array.from(alertsByAccount.values())
  if (alerts.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-16 z-[9999] flex max-h-[calc(100vh-5rem)] flex-col items-center gap-3 overflow-y-auto px-3"
      aria-live="assertive"
    >
      {alerts.map((alert) => (
        <div
          key={alert.accountId}
          className="pointer-events-auto relative liquidation-warning-card flex w-[min(92vw,420px)] flex-col items-center gap-3 rounded-lg border-2 border-danger/90 bg-black px-8 py-6 text-center text-white shadow-[0_0_38px_rgba(231,81,90,0.5)]"
          role="alert"
        >
          <button
            type="button"
            aria-label="Dismiss liquidation alert"
            className="absolute right-3 top-3 text-white/70 hover:text-white"
            onClick={() => dismissAlert(alert.accountId)}
          >
            <IoCloseOutline size={20} />
          </button>
          <AiFillWarning size={52} className="text-danger" />
          <div className="text-xl font-semibold uppercase tracking-wide text-danger">
            Liquidation
          </div>
          <div className="text-base text-white/90">You got liquidated.</div>
          {alert.occurrences > 1 && (
            <div className="text-xs text-white/60">
              {alert.occurrences} liquidation events for this account
            </div>
          )}
          <button
            type="button"
            className="mt-1 rounded-md bg-danger px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-danger/85"
            onClick={() => switchToAffectedAccount(alert)}
          >
            Switch to affected account
          </button>
        </div>
      ))}
    </div>
  )
}
